import { Component, Fragment } from 'react'

import classNames from 'classnames/bind'
import capitalize from 'lodash/capitalize'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import PropTypes from 'prop-types'

import { OrderStatus } from '__generated__/atlas-types'

import {
  FORMAT_MM_DD_YYYY,
  formatDate,
  formatTimestamp,
  isDateAfter,
} from 'utils/dates'
import {
  decimalWithCommas,
  formatNameWithCode,
  numberWithCommas,
} from 'utils/formatters'
import { getLinkToOrderReview2023 } from 'utils/links'
import { fromGlobalId } from 'utils/transformations/graphql'
import {
  getExportStatus,
  isModifiedSinceLastExport,
} from 'utils/transformations/orders'

import Checkbox from 'components/Core/Checkbox/Checkbox'
import Icon from 'components/Core/Icon/Icon'
import SummaryPopup from 'components/Core/SummaryPopup/SummaryPopup'
import Indicator from 'components/Core/Table/Indicator'
import Table from 'components/Core/Table/Table'
import Tooltip from 'components/Core/Tooltip/Tooltip'

import styles from './OrderTable.component.module.css'
import {
  getFilteredOrders,
  getOrderTotalWithCurrency,
  getStatusMapping,
  updateDueTotalPriceByCurrency,
} from './OrderTable.computations'
import messages from './OrderTable.messages'
import {
  PaymentStatuses,
  RequestStatuses,
} from 'features/Orders/ListView/components/JoorPayOrders/joorPayOrders.types'

const TOOLTIP_DATE_FORMAT = 'MM/DD/YY h:mm A'

const SMALL = 'small'
const MEDIUM = 'medium'
const DATE = 'date'
const TOTAL = 'total-order-table'

const ASCENDING = 'ASCENDING'
const DESCENDING = 'DESCENDING'

export default class OrderTable extends Component {
  state = {
    dueTotalByCurrency: updateDueTotalPriceByCurrency({
      initialPriceAcc: {},
      orders: getFilteredOrders({
        orders: this.props.orders,
        selectedOrdersIds: this.props.selectedOrdersIds,
      }),
      selectedOrdersIds: [],
    }),
  }

  componentDidUpdate(prevProps) {
    const { selectedOrdersIds } = this.props
    const { selectedOrdersIds: prevSelectedOrderIds } = prevProps
    if (prevSelectedOrderIds.length && !selectedOrdersIds.length) {
      this.initializeState()
    }
  }

  defaultFields = (sortField, direction) => [
    { field: sortField, order: direction },
  ]

  getPoHeader = () => ({
    poNumber: {
      description: messages.poNumber,
      width: SMALL,
      sortField: 'PO_NUMBER',
      fields: this.defaultFields,
    },
  })

  getStatusHeader = () => ({
    status: {
      description: messages.status,
      width: SMALL,
      fields: this.defaultFields,
    },
  })

  getUnitsHeader = () => ({
    units: {
      description: messages.units,
      width: SMALL,
      rightAlign: true,
      sortField: 'TOTAL_UNITS',
      fields: this.defaultFields,
    },
  })

  getTotalHeader = () => ({
    total: {
      description: this.getTotalColumnDescription(),
      width: TOTAL,
      rightAlign: true,
      sortField: 'DISCOUNTED_PRICE',
      fields: (sortField, direction) => [
        {
          field: 'CURRENCY',
          order: direction === ASCENDING ? DESCENDING : ASCENDING,
        },
        { field: sortField, order: direction },
      ],
      customClassName: styles.withSummaryPopup,
    },
  })

  getCollectionHeader = () => ({
    linesheet: {
      description: messages.linesheet,
      width: MEDIUM,
      sortField: 'COLLECTION_NAME',
      fields: this.defaultFields,
    },
  })

  getCompleteShipHeader = () => ({
    completeShip: {
      description: <span className="isolate">{messages.completeShip}</span>,
      width: DATE,
      sortField: 'SHIPPING_COMPLETE',
      fields: this.defaultFields,
    },
  })

  getModifiedHeader = () => ({
    modified: {
      description: messages.modified,
      width: DATE,
      sortField: 'MODIFIED',
      fields: this.defaultFields,
    },
  })

  getHeaderColumns = () => {
    const {
      flags: { joorPayUseReach },
      orderPaymentRecordsReady,
      invoicesReady,
      permissions: { allowInvoiceRetailers },
      shouldDisplayDivisionColumn,
      orderDivisionsLoading,
    } = this.props
    return {
      retailer: {
        description: messages.retailer,
        width: MEDIUM,
        sortField: 'RETAILER',
        fields: this.defaultFields,
      },
      shippingCode: {
        description: <span className="isolate">{messages.shippingCode}</span>,
        width: SMALL,
        sortField: 'SHIPPING_CODE',
        fields: this.defaultFields,
      },
      ...this.getStatusHeader(),
      priceType: {
        description: messages.priceType,
        width: MEDIUM,
        fields: this.defaultFields,
      },
      exportStatus: {
        description: <span className="isolate">{messages.exportStatus}</span>,
        width: SMALL,
        fields: this.defaultFields,
      },
      ...this.getPoHeader(),
      ...this.getCompleteShipHeader(),
      ...this.getUnitsHeader(),
      ...this.getTotalHeader(),
      ...(orderPaymentRecordsReady &&
        joorPayUseReach &&
        allowInvoiceRetailers && {
          paymentStatus: {
            description: messages.paymentStatus,
            width: TOTAL,
            rightAlign: true,
          },
        }),
      ...(invoicesReady &&
        joorPayUseReach &&
        allowInvoiceRetailers && {
          invoiceStatus: {
            description: messages.invoiceStatus,
            width: TOTAL,
            rightAlign: true,
          },
        }),
      ...(this.displayBudgets() && {
        budgets: {
          description: messages.budgets,
          width: TOTAL,
          rightAlign: true,
          fields: this.defaultFields,
        },
      }),
      ...this.getCollectionHeader(),
      salesRep: {
        description: <span className="isolate">{messages.salesRep}</span>,
        width: SMALL,
        sortField: 'SALES_REPRESENTATIVE_NAME',
        fields: this.defaultFields,
      },
      event: {
        description: messages.event,
        width: SMALL,
        sortField: 'EVENT',
        fields: this.defaultFields,
      },
      ...this.getModifiedHeader(),
      type: {
        description: messages.type,
        width: SMALL,
        sortField: 'TYPE',
        fields: this.defaultFields,
      },
      ...(shouldDisplayDivisionColumn &&
        !orderDivisionsLoading && {
          divisionCode: {
            description: (
              <span className="isolate">{messages.divisionCode}</span>
            ),
            width: SMALL,
            fields: this.defaultFields,
          },
        }),
      warehouse: {
        description: messages.warehouse,
        width: SMALL,
        fields: this.defaultFields,
      },
    }
  }

  getRetailerHeaderColumns = () => ({
    brand: {
      description: 'Brand',
      width: MEDIUM,
      sortField: 'BRAND_NAME',
      fields: this.defaultFields,
    },
    ...this.getPoHeader(),
    ...this.getStatusHeader(),
    ...this.getUnitsHeader(),
    ...this.getTotalHeader(),
    ...this.getCollectionHeader(),
    ...this.getCompleteShipHeader(),
    buyer: {
      description: 'Buyer',
      width: MEDIUM,
      sortField: 'BUYER',
      fields: this.defaultFields,
    },
    ...this.getModifiedHeader(),
  })

  getTotalColumnDescription = () => {
    const { selectedOrdersIds, trackInteractionOnCalculator } = this.props
    const { dueTotalByCurrency } = this.state
    const emptyTotalDiscount = isEmpty(dueTotalByCurrency)
    const emptyTotalDiscountBody = [[messages.totalDiscountError]]

    const summaryPopup = !isEmpty(selectedOrdersIds) ? (
      <SummaryPopup
        trigger={
          <span>
            <Icon
              name="sigma"
              className={styles.sigmaIcon}
              onClick={trackInteractionOnCalculator}
            />
          </span>
        }
        className={styles.summaryPopup}
        header={
          <SummaryPopup.Header>
            <span className={styles.summaryPopupHeader}>
              {messages.wholesaleCalculatorTitle}
            </span>
          </SummaryPopup.Header>
        }
      >
        <SummaryPopup.Body
          content={
            emptyTotalDiscount
              ? emptyTotalDiscountBody
              : this.formatDueTotalCurrency(dueTotalByCurrency)
          }
          columnClassName={
            emptyTotalDiscount
              ? styles.emptyTotalDiscountColumns
              : styles.summaryColumns
          }
          className={
            emptyTotalDiscount ? styles.emptyTotalDiscountBody : undefined
          }
          noTranslate={!emptyTotalDiscount}
        />
      </SummaryPopup>
    ) : (
      <Icon name="sigma-disabled" className={styles.sigmaIcon} />
    )

    return (
      <div className={styles.totalColumnContainer}>
        {summaryPopup}
        <span>{messages.total}</span>
      </div>
    )
  }

  getOrderByFields = (currentField) => {
    const { orderBy } = this.props
    const orderByData = orderBy.find(
      (orderByItem) => orderByItem.field === currentField,
    )
    return orderByData
      ? { field: orderByData.field, direction: orderByData.order }
      : { field: '', direction: DESCENDING }
  }

  isOrderNewerThanLastLogin = (order) => {
    const { currentUser } = this.props
    return isDateAfter(order.createdAt, currentUser.lastLogin)
  }

  initializeState = () => {
    this.setState({
      dueTotalByCurrency: updateDueTotalPriceByCurrency({
        initialPriceAcc: {},
        orders: [],
        selectedOrdersIds: [],
      }),
    })
  }

  handleSortOrders = (headerColumn) => {
    const { handlePaginationReset, sortOrders } = this.props
    const { direction } = this.getOrderByFields(headerColumn.sortField)
    const changedSortDirection =
      direction === ASCENDING ? DESCENDING : ASCENDING
    sortOrders(
      headerColumn.fields(headerColumn.sortField, changedSortDirection),
    )
    handlePaginationReset()
  }

  selectOrder = (order) => {
    const { toggleOrder, selectedOrdersIds } = this.props

    this.setState(({ dueTotalByCurrency }) => ({
      dueTotalByCurrency: updateDueTotalPriceByCurrency({
        initialPriceAcc: dueTotalByCurrency,
        orders: [order],
        selectedOrdersIds,
      }),
    }))

    toggleOrder(order.id)
  }

  selectAllOrders = (orderIds, allOrdersWereChecked) => {
    const { toggleSelectOrdersOnPage, orders, selectedOrdersIds } = this.props

    this.setState(({ dueTotalByCurrency }) => ({
      dueTotalByCurrency: updateDueTotalPriceByCurrency({
        initialPriceAcc: dueTotalByCurrency,
        orders,
        selectedOrdersIds,
        selectAll: true,
        allOrdersAreChecked: !allOrdersWereChecked,
      }),
    }))

    toggleSelectOrdersOnPage(orderIds)
  }

  formatDueTotalCurrency = (dueTotalByCurrency) =>
    Object.entries(dueTotalByCurrency).map(([currency, { value }]) => {
      const formattedCurrencyTotalDiscountedPrice = decimalWithCommas(value)
      return [formattedCurrencyTotalDiscountedPrice, currency]
    })

  displayBudgets = () => !!this.props.budgetsTotalCount

  renderTooltipWrapper = (order, dateFormat, cell) =>
    order.latestExports && order.latestExports.length ? (
      <Tooltip
        hoverable
        key={order.id}
        trigger={cell}
        wide="very"
        position="right center"
        content={this.renderExportStatusTooltip(
          order.modified,
          order.latestExports,
          dateFormat,
        )}
      />
    ) : (
      cell
    )

  getTooltipMessage = (isPaymentInfoLoading, loadingMessage) => {
    if (isPaymentInfoLoading) {
      return <span>{loadingMessage}</span>
    }

    return (
      <span>
        {messages.errorLoading}
        <br />
        {messages.openOrderToView}
      </span>
    )
  }

  renderPaymentStatusTooltipContent = (payments, dateFormat) =>
    payments.map(
      ({ status, amount, currencyCode, date, currency, paymentDate }) => (
        <span key={`${date}${amount}${currencyCode}`}>
          {`${status === 'Refunded' ? '-' : ''}
          ${numberWithCommas(amount.toFixed(2))}
          ${currencyCode ?? currency} 
          (${formatDate(date ?? paymentDate, dateFormat)})`}
          <br />
        </span>
      ),
    )

  renderInvoiceStatusTooltipContent = (invoices = []) =>
    invoices.map(({ sentOn }) => (
      <span key={sentOn}>
        {formatTimestamp(sentOn)}
        <br />
      </span>
    ))

  renderPaymentStatusTooltip = (order, dateFormat, cell) => {
    const { isPaymentInfoLoading, hasPaymentInfoErrors } = this.props

    if (isPaymentInfoLoading || hasPaymentInfoErrors) {
      const message = this.getTooltipMessage(
        isPaymentInfoLoading,
        messages.paymentStatusLoading,
      )
      return (
        <Tooltip
          hoverable
          key={`${order.id}-${order.position}`}
          trigger={cell}
          wide="very"
          position="right center"
          content={message}
        />
      )
    }

    return get(order, 'paymentRecords.payments', []).length ? (
      <Tooltip
        hoverable
        key={`${order.id}-${order.position}`}
        trigger={cell}
        wide="very"
        position="right center"
        content={this.renderPaymentStatusTooltipContent(
          get(order, 'paymentRecords.payments', []),
          dateFormat,
        )}
      />
    ) : (
      cell
    )
  }

  renderInvoiceStatusTooltip = (order, cell) => {
    const { isPaymentInfoLoading, hasPaymentInfoErrors } = this.props

    if (isPaymentInfoLoading || hasPaymentInfoErrors) {
      const message = this.getTooltipMessage(
        isPaymentInfoLoading,
        messages.invoiceStatusLoading,
      )
      return (
        <Tooltip
          hoverable
          trigger={cell}
          wide="very"
          position="right center"
          content={message}
        />
      )
    }

    if (
      order?.invoices?.length === 0 &&
      order?.lineItems?.items?.some((item) => item.quantity > 0)
    ) {
      return (
        <Tooltip
          hoverable
          trigger={cell}
          wide="very"
          position="right center"
          content="If you have sent this request, check with recipient as it may not have been received"
        />
      )
    }

    return get(order, 'invoices.length', 0) ? (
      <Tooltip
        hoverable
        trigger={cell}
        wide="very"
        position="right center"
        content={this.renderInvoiceStatusTooltipContent(order.invoices)}
      />
    ) : (
      cell
    )
  }

  renderExportStatusTooltip = (modified, latestExports, dateFormat) => {
    const tooltip = latestExports
      .map((latestExport) => {
        let statusMessage =
          `<span>${capitalize(
            latestExport.status,
          )}</span> <span notranslate>| ${latestExport.name}` +
          ` (${formatDate(latestExport.created, dateFormat)}).</span>`
        if (latestExport.exportDescription) {
          statusMessage += ` <span>${latestExport.exportDescription.replace(
            /''/g,
            '"',
          )}</span>`
        }

        if (
          latestExport.status !== 'SCHEDULED' &&
          isDateAfter(modified, latestExport.created)
        ) {
          statusMessage += `<br/>&emsp;&emsp;<span>${
            messages.modifiedSinceLastExport
          } (<var>${formatDate(modified, dateFormat)}</var>).</span>`
        }

        return statusMessage
      })
      .join('<br/>')

    // we want to render the links stored in our DB so users have an easier time
    // addressing error messages rendered in this tooltip
    // eslint-disable-next-line react/no-danger
    return <span dangerouslySetInnerHTML={{ __html: tooltip }} />
  }

  renderOrderTotalPrice = (order) =>
    getOrderTotalWithCurrency(order, order.financials?.dueTotal?.maxPrice)

  renderBudget = (order) => getOrderTotalWithCurrency(order, order.budgetsTotal)

  renderSelectOrderCheckbox = (order, orderIsChecked) => {
    return order.status !== OrderStatus.IN_PROGRESS ? (
      <Checkbox
        onClick={() => this.selectOrder(order)}
        checked={orderIsChecked}
      />
    ) : (
      ' '
    )
  }

  renderIndicator = (order) => {
    const { indicator } = this.props

    return (
      indicator && (
        <Indicator>
          {order.indicatorInfo.map(
            ({
              color,
              tooltip: indicatorTooltip,
              className: itemClassName,
            }) => (
              <Indicator.Item
                key={`${color}-${indicatorTooltip}-${order.id}`}
                tooltip={indicatorTooltip}
                className={itemClassName}
                color={color}
              />
            ),
          )}
        </Indicator>
      )
    )
  }

  renderPoCell = (order, cellProps) => (
    <Table.Cell {...cellProps} className="notranslate" width="small">
      {order?.referenceNumber}
    </Table.Cell>
  )

  renderStatusCell = (status, cellProps) => (
    <Table.Cell {...cellProps} width="small">
      {status}
    </Table.Cell>
  )

  renderUnitsCell = (order, cellProps) => (
    <Table.Cell rightAlign width="small" className="notranslate" {...cellProps}>
      {order?.lineItems && numberWithCommas(order?.lineItems?.totalUnits)}
    </Table.Cell>
  )

  renderTotalPriceCell = (order, cellProps) => (
    <Table.Cell
      rightAlign
      width="total-order-table"
      className="notranslate"
      {...cellProps}
    >
      {order?.priceType &&
        order?.lineItems &&
        this.renderOrderTotalPrice(order)}
    </Table.Cell>
  )

  renderCollectionCell = (order, cellProps) => (
    <Table.Cell className="notranslate" width="medium" {...cellProps}>
      {order?.collectionNames?.join(', ')}
    </Table.Cell>
  )

  renderCompleteShipCell = (order, cellProps) => {
    const { dateFormat } = this.props

    return (
      <Table.Cell className="notranslate" width="date" {...cellProps}>
        {formatDate(order?.deliveryComplete, dateFormat?.toUpperCase(), true)}
      </Table.Cell>
    )
  }

  renderLastModifiedCell = (order, cellProps) => {
    const { dateFormat } = this.props

    return (
      <Table.Cell className="notranslate" width="date" {...cellProps}>
        {formatDate(order?.modified, dateFormat?.toUpperCase(), true)}
      </Table.Cell>
    )
  }

  renderBrandOrders = () => {
    const {
      isOrderSelected,
      orders,
      flags: { joorPayUseReach, deprecateCartCheckout },
      orderPaymentRecordsReady,
      permissions: { allowInvoiceRetailers },
      invoicesReady,
      hasPaymentInfoErrors,
      shouldDisplayDivisionColumn,
      orderDivisionsLoading,
      isEditApprovedShippedRestricted,
    } = this.props

    const statusMapping = getStatusMapping()
    return orders.map((order) => {
      const orderIsChecked = isOrderSelected(order.id)
      const link = getLinkToOrderReview2023({
        order: { ...order, id: fromGlobalId(order.id)?.id },
        accountType: 'brand',
        isEditApprovedShippedRestricted: isEditApprovedShippedRestricted,
        isCartDeprecated: deprecateCartCheckout,
      })
      const { salesRepresentative } = order

      const orderRowsProps = {
        className: classNames({
          [styles.selectedRow]: orderIsChecked,
        }),
      }

      const commonCellProps = {
        link,
        shouldLinkOpenInNewTab: true,
      }

      const orderRowsChildren = (
        <>
          <Table.Cell>
            {this.renderSelectOrderCheckbox(order, orderIsChecked)}
          </Table.Cell>
          <Table.Cell
            className="notranslate"
            width="medium"
            {...commonCellProps}
          >
            {order.orphanRetailer
              ? order.orphanRetailer.retailerName
              : order.retailerName}
          </Table.Cell>
          <Table.Cell
            className="notranslate"
            width="small"
            {...commonCellProps}
          >
            {order.shippingAddress && order.shippingAddress.code}
          </Table.Cell>
          {this.renderStatusCell(statusMapping[order.status], commonCellProps)}
          <Table.Cell
            className="notranslate"
            width="medium"
            {...commonCellProps}
          >
            {formatNameWithCode(
              order.priceType?.name,
              order.priceType?.currency?.code,
            )}
          </Table.Cell>
          {this.renderTooltipWrapper(
            order,
            TOOLTIP_DATE_FORMAT,
            <Table.Cell
              disableTooltip
              className={classNames(styles[getExportStatus(order)], {
                [styles.modifiedSinceLastExport]:
                  order.latestExports.length > 0 &&
                  typeof order.modified !== 'undefined' &&
                  isModifiedSinceLastExport(
                    order.latestExports,
                    order.modified,
                  ),
              })}
              link={link}
              shouldLinkOpenInNewTab
              width="small"
            >
              {order.latestExports && statusMapping[getExportStatus(order)]}
            </Table.Cell>,
          )}
          {this.renderPoCell(order, commonCellProps)}
          {this.renderCompleteShipCell(order, commonCellProps)}
          {this.renderUnitsCell(order, commonCellProps)}
          {this.renderTotalPriceCell(order, commonCellProps)}
          {orderPaymentRecordsReady &&
            joorPayUseReach &&
            allowInvoiceRetailers &&
            this.renderPaymentStatusTooltip(
              order,
              TOOLTIP_DATE_FORMAT,
              <Table.Cell centerAlign>
                {hasPaymentInfoErrors
                  ? messages.error
                  : PaymentStatuses[order.paymentStatus]}
              </Table.Cell>,
            )}
          {invoicesReady &&
            joorPayUseReach &&
            allowInvoiceRetailers &&
            this.renderInvoiceStatusTooltip(
              order,
              <Table.Cell centerAlign>
                {hasPaymentInfoErrors
                  ? messages.error
                  : RequestStatuses[order.requestStatus]}
              </Table.Cell>,
            )}
          {this.displayBudgets() && (
            <Table.Cell
              rightAlign
              width="total-order-table"
              className="notranslate"
              {...commonCellProps}
            >
              {order.priceType &&
                order.budgetsTotal &&
                this.renderBudget(order)}
            </Table.Cell>
          )}
          {this.renderCollectionCell(order, commonCellProps)}
          <Table.Cell
            className="notranslate"
            tooltipNotranslate
            width="small"
            {...commonCellProps}
          >
            {salesRepresentative &&
              formatNameWithCode(
                salesRepresentative.name,
                salesRepresentative.code,
              )}
          </Table.Cell>
          <Table.Cell
            className="notranslate"
            width="small"
            {...commonCellProps}
          >
            {formatNameWithCode(order.eventName, order.eventCode)}
          </Table.Cell>
          {this.renderLastModifiedCell(order, commonCellProps)}
          <Table.Cell
            className="notranslate"
            width="small"
            {...commonCellProps}
          >
            {order.type
              .map((type) => formatNameWithCode(type.name, type.code))
              .join(', ')}
          </Table.Cell>
          {shouldDisplayDivisionColumn && !orderDivisionsLoading && (
            <Table.Cell
              className="notranslate"
              width="small"
              {...commonCellProps}
            >
              {order.divisionCodes && order.divisionCodes.join(', ')}
            </Table.Cell>
          )}
          <Table.Cell
            className="notranslate"
            width="small"
            {...commonCellProps}
          >
            {formatNameWithCode(order?.warehouse?.name, order?.warehouse?.code)}
          </Table.Cell>
        </>
      )

      return (
        <Fragment key={order.id}>
          <Table.Row
            {...orderRowsProps}
            className={classNames({
              ...orderRowsProps.className,
              [styles.createdByRetailerOrder]:
                this.isOrderNewerThanLastLogin(order) &&
                get(order, 'createdByRetailer', false),
            })}
          >
            {this.renderIndicator(order)}
            {orderRowsChildren}
          </Table.Row>
        </Fragment>
      )
    })
  }

  renderRetailerOrders = () => {
    const {
      isOrderSelected,
      orders,
      isEditApprovedShippedRestricted,
      flags: { deprecateCartCheckout },
    } = this.props

    const statusMapping = getStatusMapping()

    return orders.map((order) => {
      const orderIsChecked = isOrderSelected(order.id)
      const link = getLinkToOrderReview2023({
        order: { ...order, id: fromGlobalId(order.id)?.id },
        accountType: 'retailer',
        isEditApprovedShippedRestricted: isEditApprovedShippedRestricted,
        isCartDeprecated: deprecateCartCheckout,
      })

      const orderRowsProps = {
        className: classNames({
          [styles.selectedRow]: orderIsChecked,
        }),
      }

      const commonCellProps = {
        link,
        shouldLinkOpenInNewTab: true,
      }

      const orderRowsChildren = (
        <>
          <Table.Cell>
            {this.renderSelectOrderCheckbox(order, orderIsChecked)}
          </Table.Cell>
          <Table.Cell
            {...commonCellProps}
            className="notranslate"
            width="medium"
          >
            {order.brand?.name}
          </Table.Cell>
          {this.renderPoCell(order, commonCellProps)}
          {this.renderStatusCell(statusMapping[order.status], commonCellProps)}
          {this.renderUnitsCell(order, commonCellProps)}
          {this.renderTotalPriceCell(order, commonCellProps)}
          {this.renderCollectionCell(order, commonCellProps)}
          {this.renderCompleteShipCell(order, commonCellProps)}
          <Table.Cell
            {...commonCellProps}
            className="notranslate"
            width="medium"
          >
            {order.shippingName}
          </Table.Cell>
          {this.renderLastModifiedCell(order, commonCellProps)}
        </>
      )

      return (
        <Fragment key={order.id}>
          <Table.Row {...orderRowsProps}>{orderRowsChildren}</Table.Row>
        </Fragment>
      )
    })
  }

  renderOrderRows = () =>
    this.props.isBrandUser
      ? this.renderBrandOrders()
      : this.renderRetailerOrders()

  renderSelectAllOrdersCheckbox = (orderIds, allOrdersAreChecked) => (
    <Checkbox
      onClick={() => this.selectAllOrders(orderIds, allOrdersAreChecked)}
      checked={allOrdersAreChecked}
    />
  )

  renderHeader = ({ allOrdersAreChecked, sortable, orderIds }) => {
    const { indicator, isBrandUser, selectedFilters } = this.props
    const headerColumns = isBrandUser
      ? this.getHeaderColumns()
      : this.getRetailerHeaderColumns()
    const isDraftOrderFilterSelected = selectedFilters?.some(
      (filter) => filter.id === OrderStatus.IN_PROGRESS,
    )
    const showSelectAll =
      !this.props.hasDraftOrders ||
      (this.props.hasDraftOrders && !isDraftOrderFilterSelected)
    return (
      <Table.Row>
        {indicator && <Table.HeaderCell width="indicator"> </Table.HeaderCell>}
        {showSelectAll ? (
          <Table.HeaderCell width="checkbox">
            {this.renderSelectAllOrdersCheckbox(orderIds, allOrdersAreChecked)}
          </Table.HeaderCell>
        ) : (
          <Table.HeaderCell width="checkbox" />
        )}
        {Object.keys(headerColumns).map((key) => {
          const headerColumn = headerColumns[key]
          const { field, direction } =
            sortable && this.getOrderByFields(headerColumn.sortField)
          const headerCellProps = {
            key,
            width: headerColumn.width,
            rightAlign: headerColumn.rightAlign,
            [headerColumn.customClassName && 'className']:
              headerColumn.customClassName,
          }

          if (sortable && headerColumn.sortField) {
            headerCellProps.sortDirection = direction.toLowerCase()
            headerCellProps.onClick = () => this.handleSortOrders(headerColumn)
            headerCellProps.isSorted = field === headerColumn.sortField
          }

          return (
            <Table.HeaderCell {...headerCellProps}>
              {headerColumn.description}
            </Table.HeaderCell>
          )
        })}
      </Table.Row>
    )
  }

  renderOrderTable = (props) => (
    <Table sortable={props.sortable} compact className={styles.orderListTable}>
      <Table.Header sticky>{this.renderHeader(props)}</Table.Header>
      <Table.Body>{this.renderOrderRows()}</Table.Body>
    </Table>
  )

  render() {
    const { orders, allOrdersAreSelected } = this.props
    const orderIds = orders.map((order) => order.id)
    const allOrdersAreChecked = allOrdersAreSelected(orderIds)
    const orderTableProps = {
      orderIds,
      allOrdersAreChecked,
      sortable: true,
    }

    return this.renderOrderTable(orderTableProps)
  }
}

OrderTable.propTypes = {
  orders: PropTypes.arrayOf(PropTypes.object).isRequired,
  dateFormat: PropTypes.string,
  allOrdersAreSelected: PropTypes.func.isRequired,
  handlePaginationReset: PropTypes.func.isRequired,
  isOrderSelected: PropTypes.func.isRequired,
  toggleOrder: PropTypes.func.isRequired,
  toggleSelectOrdersOnPage: PropTypes.func.isRequired,
  sortOrders: PropTypes.func.isRequired,
  orderBy: PropTypes.arrayOf(PropTypes.object).isRequired,
  location: PropTypes.object.isRequired,
  selectedOrdersIds: PropTypes.array,
  trackInteractionOnCalculator: PropTypes.func,
  indicator: PropTypes.bool,
  currentUser: PropTypes.object.isRequired,
  budgetsTotalCount: PropTypes.number,
  flags: PropTypes.shape({
    joorPayUseReach: PropTypes.bool,
  }),
  permissions: PropTypes.shape({
    allowInvoiceRetailers: PropTypes.bool,
  }),
  isBrandUser: PropTypes.bool.isRequired,
  hasDraftOrders: PropTypes.bool,
  selectedFilters: PropTypes.arrayOf(PropTypes.object),
  isEditApprovedShippedRestricted: PropTypes.bool,
}

OrderTable.defaultProps = {
  dateFormat: FORMAT_MM_DD_YYYY,
  selectedOrdersIds: [],
  trackInteractionOnCalculator: () => {},
  indicator: false,
  budgetsTotalCount: 0,
  flags: {
    joorPayUseReach: false,
  },
  permissions: {},
}
